import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from './notify';
import { Stethoscope, FlaskConical, ScanLine, Pill, Briefcase, Trash2, X, ClipboardCheck, Activity, Search, BedDouble, UserRoundPlus, UserRound } from 'lucide-react';
import { VisitCard, ItemsTable, money, ApproverRemovals, formatVitals, VITAL_FIELDS, DOCTOR_ROLES, deptForRole, visitDept, deptLabel, roleCovers, SPECIALISTS, SPECIALIST_INTERNAL_DEPT, REFERRAL_URGENCIES } from './VisitBits';
import { findFeeItem, FEE_LABELS } from './consultFees';
import PatientHistory from './PatientHistory';
import InpatientPanel from './InpatientPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BucketList = ({ title, icon: Icon, items, setItems, visit, withInstructions, testPrefix, hint }) => (
  <div className="border border-gray-200 rounded-lg p-3">
    <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
      <Icon className="w-4 h-4" /> {title} ({items.length})
    </p>
    {items.length === 0 && <p className="text-xs text-gray-400">{hint || 'No items added'}</p>}
    {items.map((item, idx) => (
      <div key={idx} className="flex items-center gap-2 py-1.5 border-t border-gray-100 first:border-t-0" data-testid={`${testPrefix}-row-${idx}`}>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 truncate">{item.item_name}</p>
          {withInstructions && (
            <Input
              placeholder="Dosage / instructions"
              value={item.instructions}
              onChange={(e) => setItems((prev) => prev.map((p, i) => (i === idx ? { ...p, instructions: e.target.value } : p)))}
              className="h-7 text-xs mt-1"
              data-testid={`${testPrefix}-instructions-${idx}`}
            />
          )}
        </div>
        <Input
          type="number"
          min="1"
          value={item.quantity}
          onChange={(e) => setItems((prev) => prev.map((p, i) => (i === idx ? { ...p, quantity: Math.max(1, parseInt(e.target.value) || 1) } : p)))}
          className="h-8 w-16 text-center"
          data-testid={`${testPrefix}-qty-${idx}`}
        />
        <span className="text-sm text-gray-600 w-20 text-right">{money(visit, item.cost * item.quantity)}</span>
        <button onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700" data-testid={`${testPrefix}-remove-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    ))}
  </div>
);

const TextArea = ({ label, required, value, onChange, placeholder, testId, rows = 2 }) => {
  const ref = React.useRef(null);
  const cursorRef = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
    if (cursorRef.current != null) {
      el.selectionStart = el.selectionEnd = cursorRef.current;
      cursorRef.current = null;
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    const el = e.target;
    const before = value.slice(0, el.selectionStart);
    const after = value.slice(el.selectionEnd);
    const lineStart = before.lastIndexOf('\n') + 1;
    const currentLine = before.slice(lineStart);
    if (!currentLine.replace(/^\s*\d+[.)]\s*/, '').trim()) {
      // Enter on an empty item exits the list (removes the dangling number)
      cursorRef.current = lineStart;
      onChange({ target: { value: before.slice(0, lineStart) + after } });
      return;
    }
    const newLineIdx = before.split('\n').length; // index of the fresh empty line
    const numbered = (before + '\n' + after)
      .split('\n')
      .map((l, i) => `${i + 1}. ${l.replace(/^\s*\d+[.)]\s*/, '')}`);
    cursorRef.current = numbered.slice(0, newLineIdx + 1).join('\n').length;
    onChange({ target: { value: numbered.join('\n') } });
  };

  return (
    <div>
      <Label className="text-gray-700 font-medium">{label}{required ? ' *' : ''}</Label>
      <textarea
        ref={ref}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        rows={rows}
        className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:outline-none resize-none overflow-hidden"
        placeholder={placeholder}
        data-testid={testId}
      />
      <p className="text-[11px] text-gray-400 mt-0.5">Press Enter to add a numbered item</p>
    </div>
  );
};

const cleanList = (text) => text.split('\n').filter((l) => l.replace(/^\s*\d+[.)]\s*/, '').trim()).join('\n');

const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const draftKey = (visitId) => `consult_draft_${localStorage.getItem('username') || ''}_${visitId}`;


export default function ConsultationPanel({ visits, refresh, authCfg, role, isSuperAdmin }) {
  const canAct = isSuperAdmin || DOCTOR_ROLES.includes(role) || role === 'Admin';
  const myDept = deptForRole(role);
  const [queueSearch, setQueueSearch] = useState('');
  const queue = visits.filter((v) => ['WAITING_CONSULTANT', 'IN_CONSULTATION', 'CONSULTANT_REVIEW'].includes(v.status) && roleCovers(role, v));
  const q = queueSearch.trim().toLowerCase();
  const filteredQueue = q
    ? queue.filter((v) =>
        (v.patient_name || '').toLowerCase().includes(q)
        || (v.patient_serial_number || '').toLowerCase().includes(q)
        || (v.visit_id || '').toLowerCase().includes(q))
    : queue;

  const [active, setActive] = useState(null);
  const [inpatient, setInpatient] = useState(null);
  const [admitDialog, setAdmitDialog] = useState(false);
  const [admit, setAdmit] = useState({ reason: '', ward: '', bed_item_id: '' });
  const admitted = visits.filter((v) => v.status === 'ADMITTED' && roleCovers(role, v));
  const [complaints, setComplaints] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [finalDiagnosis, setFinalDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [vitals, setVitals] = useState({});
  const [labOrders, setLabOrders] = useState([]);
  const [radOrders, setRadOrders] = useState([]);
  const [dentalOrders, setDentalOrders] = useState([]);
  const [pocTests, setPocTests] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [referral, setReferral] = useState(null);
  const [doctorName, setDoctorName] = useState(() => localStorage.getItem('doctor_full_name') || '');
  useEffect(() => {
    axios.get(`${API}/workflow/my-doctor-name`, authCfg).then((r) => {
      if (r.data?.doctor_name) { setDoctorName(r.data.doctor_name); localStorage.setItem('doctor_full_name', r.data.doctor_name); }
    }).catch(() => {});
  }, [authCfg]);
  const [referDialog, setReferDialog] = useState(false);
  const [referPick, setReferPick] = useState({ type: 'internal', specialist: '', note: '', facility: '', urgency: 'Routine', doctorName: '' });
  const [priceList, setPriceList] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const hadDraftRef = React.useRef(false);

  const isReview = active?.status === 'CONSULTANT_REVIEW';
  const isSpecialistForm = !!active && visitDept(active) !== 'general';  // 'Tests done here' for every doctor except the General Doctor

  // Auto-save draft (debounced) so the doctor's work survives tab close / power loss.
  // Saved locally AND on the server so the doctor can continue from any computer.
  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => {
      const key = draftKey(active.visit_id);
      const hasContent = complaints.trim() || diagnosis.trim() || finalDiagnosis.trim() || notes.trim()
        || labOrders.length > 0 || radOrders.length > 0 || dentalOrders.length > 0 || pocTests.length > 0 || prescriptions.length > 0 || serviceItems.length > 0 || referral
        || Object.values(vitals).some((v) => v && String(v).trim());
      if (!hasContent) {
        localStorage.removeItem(key);
        if (hadDraftRef.current) {
          axios.delete(`${API}/visits/${active.visit_id}/draft`, authCfg).catch(() => {});
          hadDraftRef.current = false;
        }
        setDraftSavedAt(null);
        return;
      }
      const draft = {
        mode: active.status === 'CONSULTANT_REVIEW' ? 'review' : 'initial',
        complaints, diagnosis, finalDiagnosis, notes, vitals,
        labOrders, radOrders, dentalOrders, pocTests, prescriptions, serviceItems, referral,
        savedAt: Date.now(),
      };
      try {
        localStorage.setItem(key, JSON.stringify(draft));
      } catch (e) { /* storage full — skip silently */ }
      axios.put(`${API}/visits/${active.visit_id}/draft`, draft, authCfg).catch(() => {});
      hadDraftRef.current = true;
      setDraftSavedAt(new Date());
    }, 500);
    return () => clearTimeout(timer);
  }, [active, complaints, diagnosis, finalDiagnosis, notes, vitals, labOrders, radOrders, dentalOrders, pocTests, prescriptions, serviceItems, referral, authCfg]);

  const clearDraft = (visitId) => {
    localStorage.removeItem(draftKey(visitId));
    hadDraftRef.current = false;
    setDraftSavedAt(null);
  };

  const openVisit = async (visit) => {
    if (visit.status === 'WAITING_CONSULTANT') {
      try {
        await axios.post(`${API}/visits/${visit.visit_id}/start-consultation`, {}, authCfg);
        refresh();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not start consultation');
        return;
      }
    }
    let fresh = visit;
    try {
      const res = await axios.get(`${API}/visits/${visit.visit_id}`, authCfg);
      fresh = res.data;
    } catch (e) { /* fall back to queue data */ }
    setActive(fresh);
    setItemSearch('');
    setVitals(fresh.vitals || {});
    setLabOrders([]);
    setRadOrders([]);
    setDentalOrders([]);
    setPocTests([]);
    setPrescriptions([]);
    if (fresh.status === 'CONSULTANT_REVIEW') {
      setComplaints(fresh.consultation?.complaints || '');
      setDiagnosis(fresh.consultation?.provisional_diagnosis || fresh.consultation?.diagnosis || '');
      setFinalDiagnosis(fresh.consultation?.final_diagnosis || '');
      setNotes(fresh.consultation?.notes || '');
      setServiceItems((fresh.service_items || []).map((s) => ({ ...s, instructions: '' })));
    } else if (fresh.referral?.approval?.status === 'rejected' && fresh.consultation?.complaints) {
      // Referral declined by the Acacia office — the visit is back with this doctor: continue from what was recorded
      setComplaints(fresh.consultation.complaints || '');
      setDiagnosis(fresh.consultation.provisional_diagnosis || fresh.consultation.diagnosis || '');
      setFinalDiagnosis('');
      setNotes(fresh.consultation.notes || '');
      setVitals(fresh.consultation.vitals || fresh.vitals || {});
      setServiceItems([]);
    } else {
      setComplaints('');
      setDiagnosis('');
      setFinalDiagnosis('');
      setNotes('');
      setServiceItems([]);
    }
    setDraftSavedAt(null);
    setReferral(null);
    hadDraftRef.current = false;
    let restored = false;
    // Restore an unsaved draft — pick the NEWEST of the local copy and the server copy
    // (server drafts let the doctor continue from any computer)
    try {
      let local = null;
      try { local = JSON.parse(localStorage.getItem(draftKey(fresh.visit_id))); } catch (e) { local = null; }
      const server = fresh.draft || null;
      const mode = fresh.status === 'CONSULTANT_REVIEW' ? 'review' : 'initial';
      const isValid = (d) => d && d.mode === mode && Date.now() - (d.savedAt || 0) < DRAFT_MAX_AGE_MS;
      let d = null;
      if (isValid(local) && isValid(server)) d = (local.savedAt >= server.savedAt) ? local : server;
      else d = isValid(local) ? local : (isValid(server) ? server : null);
      if (d) {
        restored = true;
        setComplaints(d.complaints || '');
        setDiagnosis(d.diagnosis || '');
        setFinalDiagnosis(d.finalDiagnosis || '');
        setNotes(d.notes || '');
        setVitals(d.vitals && Object.keys(d.vitals).length ? d.vitals : (fresh.vitals || {}));
        setLabOrders(d.labOrders || []);
        setRadOrders(d.radOrders || []);
        setDentalOrders(d.dentalOrders || []);
        setPocTests(d.pocTests || []);
        setPrescriptions(d.prescriptions || []);
        setServiceItems(d.serviceItems || []);
        setReferral(d.referral || null);
        setDraftSavedAt(new Date(d.savedAt));
        hadDraftRef.current = true;
        const from = d === server && d.saved_by ? ` by ${d.saved_by}` : '';
        toast.info(`Unsaved draft restored (saved ${new Date(d.savedAt).toLocaleString()}${from})`, { duration: 5000 });
      } else if (local) {
        localStorage.removeItem(draftKey(fresh.visit_id));
      }
    } catch (e) { /* corrupt draft — ignore */ }
    let items = [];
    try {
      if (isSuperAdmin) {
        const res = await axios.get(`${API}/admin/hospitals/${encodeURIComponent(fresh.hospital_name)}/pricelist`, authCfg);
        items = res.data.items || [];
      } else {
        const res = await axios.get(`${API}/pricelists`, authCfg);
        items = res.data || [];
      }
      setPriceList(items);
    } catch (e) {
      setPriceList([]);
      toast.error('Could not load the hospital price list');
      return;
    }
    if (!restored && fresh.status !== 'CONSULTANT_REVIEW' && !(fresh.service_items || []).some((s) => findFeeItem([s], visitDept(fresh)))) autoAddFee(fresh, items, visitDept(fresh));
  };

  // The consultation fee for the doctor's department is charged automatically when the consultation opens
  const autoAddFee = (visit, items, dept) => {
    if (dept !== 'general' && dept !== 'specialist') return;
    const fee = findFeeItem(items, dept);
    if (!fee) {
      toast.warning(`No "${FEE_LABELS[dept]}" item found in the hospital price list — add the consultation fee manually`);
      return;
    }
    if ((visit.service_items || []).some((s) => s.item_id === fee.item_id)) return;
    setServiceItems((prev) => (prev.some((s) => s.item_id === fee.item_id) ? prev : [...prev, { item_id: fee.item_id, item_name: fee.item_name, cost: fee.cost, quantity: 1, instructions: '' }]));
    toast.success(`Added: ${fee.item_name} (consultation fee)`);
  };

  const applyReferral = () => {
    if (!referPick.specialist) { toast.error('Choose the specialist to refer the patient to'); return; }
    const external = referPick.type === 'external';
    if (external && !referPick.facility.trim()) { toast.error('Enter the hospital/facility the patient is referred to'); return; }
    if (external && !referPick.note.trim()) { toast.error('Enter the reason for the referral'); return; }
    if (external && !referPick.doctorName.trim()) { toast.error('Enter your full name — it is printed on the referral letter'); return; }
    if (external) localStorage.setItem('doctor_full_name', referPick.doctorName.trim());
    let feeId = null;
    let replacedFee = null;
    if (!external && !SPECIALIST_INTERNAL_DEPT[referPick.specialist]) {
      const fee = findFeeItem(priceList, 'specialist');
      const generalFee = findFeeItem(priceList, 'general');
      replacedFee = generalFee ? serviceItems.find((s) => s.item_id === generalFee.item_id) || null : null;
      if (fee) {
        feeId = fee.item_id;
        setServiceItems((prev) => {
          const rest = prev.filter((s) => !generalFee || s.item_id !== generalFee.item_id);
          return rest.some((s) => s.item_id === fee.item_id) ? rest : [...rest, { item_id: fee.item_id, item_name: fee.item_name, cost: fee.cost, quantity: 1, instructions: '' }];
        });
        if (replacedFee) toast.info(`"${replacedFee.item_name}" replaced with "${fee.item_name}"`);
      } else toast.warning(`No "${FEE_LABELS.specialist}" item found in the hospital price list — add the specialist fee manually`);
    }
    setReferral({ type: referPick.type, specialist: referPick.specialist, note: referPick.note.trim(), facility: referPick.facility.trim(), urgency: referPick.urgency, doctor_name: referPick.doctorName.trim(), fee_item_id: feeId, replaced_fee: replacedFee });
    setReferDialog(false);
    if (external) toast.success(`External referral to ${referPick.specialist} at ${referPick.facility.trim()} added — sent to the Acacia office for approval when you complete this consultation`);
    else toast.success(`Referral to ${referPick.specialist} added — after this consultation the Acacia office approves it and the patient joins the ${SPECIALIST_INTERNAL_DEPT[referPick.specialist] ? referPick.specialist : 'Specialist'} queue`);
  };

  const clearReferral = () => {
    if (referral?.fee_item_id) {
      setServiceItems((prev) => {
        const rest = prev.filter((s) => s.item_id !== referral.fee_item_id);
        const back = referral.replaced_fee;
        return back && !rest.some((s) => s.item_id === back.item_id) ? [...rest, back] : rest;
      });
    }
    setReferral(null);
  };

  const results = itemSearch
    ? priceList
        .filter((i) => i.item_name.toLowerCase().includes(itemSearch.toLowerCase()) || i.item_id.toLowerCase().includes(itemSearch.toLowerCase()))
        .slice(0, 15)
    : [];

  const addItem = (setter) => (item) => {
    setter((prev) => [...prev, { item_id: item.item_id, item_name: item.item_name, cost: item.cost, quantity: 1, instructions: '' }]);
    toast.success(`Added: ${item.item_name}`);
  };

  const addLab = (item) => {
    if (prescriptions.length > 0) {
      toast.dismiss();
      toast.error('Remove the prescriptions first — medications are prescribed after the test results return to you.');
      return;
    }
    addItem(setLabOrders)(item);
  };

  const addRad = (item) => {
    if (prescriptions.length > 0) {
      toast.dismiss();
      toast.error('Remove the prescriptions first — medications are prescribed after the test results return to you.');
      return;
    }
    addItem(setRadOrders)(item);
  };

  const addPocTest = (item) => {
    setPocTests((prev) => (prev.some((x) => x.item_id === item.item_id) ? prev : [...prev, { item_id: item.item_id, item_name: item.item_name, cost: item.cost, quantity: 1, result: '' }]));
    setItemSearch('');
  };

  const addRx = (item) => {
    if (!isReview && (labOrders.length > 0 || radOrders.length > 0 || dentalOrders.length > 0)) {
      toast.dismiss();
      toast.error('Tests are ordered first. You will prescribe medications when the results return to you.');
      return;
    }
    addItem(setPrescriptions)(item);
  };

  const referralPayload = () => (referral ? { refer_specialist: referral.specialist, referral_note: referral.note || '', referral_type: referral.type || 'internal', referral_facility: referral.facility || '', referral_urgency: referral.urgency || 'Routine', referral_doctor_name: referral.doctor_name || doctorName.trim() } : {});
  const rememberDoctorName = () => { if (doctorName.trim()) localStorage.setItem('doctor_full_name', doctorName.trim()); };
  const approvalToast = (res, ext) => {
    if (res.data.lab_approval_required || res.data.rad_approval_required) toast.warning('The ordered tests exceed the approval threshold — waiting for approval from Acacia office' + ext, { duration: ext ? 9000 : 5000 });
    else if (res.data.service_approval_required) toast.warning('The added services exceed the approval threshold — waiting for approval from Acacia office' + ext, { duration: ext ? 9000 : 5000 });
    else if (res.data.referral_approval_required) toast.warning('Referral sent to the Acacia office for approval — the patient joins the specialist queue once approved' + ext, { duration: ext ? 9000 : 6000 });
    else if (res.data.rx_approval_required) toast.warning('Prescription total exceeds the approval threshold — waiting for approval from Acacia office' + ext, { duration: ext ? 9000 : 5000 });
    else return false;
    return true;
  };

  const submitInitial = async () => {
    if (!doctorName.trim()) { toast.error('Enter your name in the Attending doctor box — it is printed on the bill and visit summary'); return; }
    if (!complaints.trim()) { toast.error('Patient complaints are required'); return; }
    if (!diagnosis.trim()) { toast.error('Provisional/Differential diagnosis is required'); return; }
    if (pocTests.some((t) => !t.result.trim())) { toast.error('Enter the result for every test done here'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/visits/${active.visit_id}/consultation`,
        {
          complaints: cleanList(complaints),
          provisional_diagnosis: cleanList(diagnosis),
          notes: cleanList(notes),
          vitals: Object.fromEntries(Object.entries(vitals).filter(([, v]) => v && v.trim())),
          lab_orders: labOrders,
          rad_orders: radOrders,
          dental_orders: dentalOrders,
          poc_tests: pocTests,
          prescriptions,
          service_items: serviceItems,
          doctor_name: doctorName.trim(),
          ...referralPayload(),
        },
        authCfg
      );
      rememberDoctorName();
      const ext = referral?.type === 'external' ? '\nExternal referral sent to the Acacia office for approval — print the letter from All Visits once approved.' : '';
      if (approvalToast(res, ext)) { /* shown */ }
      else if (res.data.next_department_label) toast.success(`Consultation saved — patient sent to the ${res.data.next_department_label} queue` + ext, { duration: ext ? 9000 : 5000 });
      else toast.success(`Consultation saved — visit moved to: ${res.data.status.replace(/_/g, ' ').toLowerCase()}` + ext, { duration: ext ? 9000 : 5000 });
      clearDraft(active.visit_id);
      setActive(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save consultation');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReview = async () => {
    if (!doctorName.trim()) { toast.error('Enter your name in the Attending doctor box — it is printed on the bill and visit summary'); return; }
    if (!finalDiagnosis.trim()) { toast.error('Final diagnosis is required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/visits/${active.visit_id}/review`,
        { final_diagnosis: cleanList(finalDiagnosis), notes: cleanList(notes), prescriptions, service_items: serviceItems, doctor_name: doctorName.trim(), ...referralPayload() },
        authCfg
      );
      rememberDoctorName();
      const ext = referral?.type === 'external' ? '\nExternal referral sent to the Acacia office for approval — print the letter from All Visits once approved.' : '';
      if (approvalToast(res, ext)) { /* shown */ }
      else if (res.data.next_department_label) toast.success(`Review saved — patient sent to the ${res.data.next_department_label} queue` + ext, { duration: ext ? 9000 : 5000 });
      else toast.success(`Review saved — visit moved to: ${res.data.status.replace(/_/g, ' ').toLowerCase()}` + ext, { duration: ext ? 9000 : 5000 });
      clearDraft(active.visit_id);
      setActive(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save review');
    } finally {
      setSubmitting(false);
    }
  };

  const requestAdmission = async () => {
    if (!admit.reason.trim()) { toast.error('Reason for admission is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/visits/${active.visit_id}/admission/request`, {
        reason: admit.reason, ward: admit.ward, bed_item_id: admit.bed_item_id || null,
        complaints: cleanList(complaints) || null, provisional_diagnosis: cleanList(diagnosis) || null, notes: cleanList(notes) || null,
        vitals: Object.fromEntries(Object.entries(vitals).filter(([, v]) => v && v.trim())),
      }, authCfg);
      toast.success('Admission requested — waiting for approval from Acacia office');
      clearDraft(active.visit_id);
      setAdmitDialog(false);
      setActive(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to request admission');
    } finally {
      setSubmitting(false);
    }
  };

  if (inpatient) {
    return <InpatientPanel visit={inpatient} authCfg={authCfg} isSuperAdmin={isSuperAdmin} onClose={() => setInpatient(null)} refresh={refresh} />;
  }

  if (active) {
    const bedItems = priceList.filter((i) => /bed|ward|admission|nursing/i.test(i.item_name)).slice(0, 30);
    const completedLabs = (active.lab_orders || []).filter((o) => o.status === 'completed');
    const rejectedLabs = (active.lab_orders || []).filter((o) => o.status === 'rejected');
    const completedRads = (active.rad_orders || []).filter((o) => o.status === 'completed');
    const rejectedRads = (active.rad_orders || []).filter((o) => o.status === 'rejected');
    const completedDentals = (active.dental_orders || []).filter((o) => o.status === 'completed');
    const rxRejected = active.rx_approval?.status === 'rejected' && active.rx_approval?.action === 'return';
    return (
      <Card className="border-indigo-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-indigo-200 p-4">
          <CardTitle className="flex items-center justify-between text-indigo-900 text-base">
            <span className="flex items-center gap-2">
              {isReview ? <ClipboardCheck className="w-5 h-5" /> : <Stethoscope className="w-5 h-5" />}
              {isReview ? 'Results Review & Prescription' : 'Consultation'} — {active.patient_name}
            </span>
            <Button variant="outline" size="sm" onClick={() => setActive(null)} data-testid="consultation-close">
              <X className="w-4 h-4 mr-1" /> Back to queue
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            {active.visit_id} • Serial: {active.patient_serial_number} • {active.patient_sex} • DOB: {active.patient_dob}
            {active.reason && <span> • Reason: <span className="font-medium text-gray-800">{active.reason}</span></span>}
          </div>

          <PatientHistory serial={active.patient_serial_number} authCfg={authCfg} currentVisitId={active.visit_id} />

          <EarlierDoctors visit={active} />

          {!isReview && active.referral?.approval?.status === 'rejected' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" data-testid="referral-rejected-banner">
              <strong>Referral to {active.referral.specialist} declined by the Acacia office</strong>{active.referral.approval.rejection_reason ? ` — ${active.referral.approval.rejection_reason}` : ''}. The patient is back with you: continue as a normal consultation — order tests, prescribe, or complete the visit to send the patient to pharmacy/billing. Your earlier notes are pre-filled.
            </div>
          )}

          <div className="border border-gray-200 bg-gray-50/60 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="attending-doctor-box">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 whitespace-nowrap"><UserRound className="w-4 h-4 text-teal-600" /> Attending doctor <span className="text-red-500">*</span></div>
            <Input value={doctorName} onChange={(e) => setDoctorName(e.target.value)} placeholder="Your full name, e.g. Dr. John Deng Garang" className="h-9 bg-white sm:max-w-md" data-testid="attending-doctor-name" />
            <p className="text-xs text-gray-500">Printed on the bill, visit summary and referral letter — remembered for your next consultation.</p>
          </div>

          {isReview ? (
            <>
              <ApproverRemovals visit={active} />
              {rxRejected && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" data-testid="rx-rejected-banner">
                  <strong>Prescription rejected by Acacia office</strong>{active.rx_approval?.rejection_reason ? ` — ${active.rx_approval.rejection_reason}` : ''}. Please revise the medication based on the lab results.
                </div>
              )}
              {formatVitals(active.consultation?.vitals) && (
                <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-900" data-testid="review-vitals">
                  <strong>Vital signs:</strong> {formatVitals(active.consultation.vitals)}
                </div>
              )}
              {active.consultation?.complaints && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 whitespace-pre-line">
                  <strong>Patient complaints:</strong> {active.consultation.complaints}
                </div>
              )}
              {completedLabs.length > 0 ? (
                <ItemsTable title="Lab Results (review before prescribing)" items={completedLabs} visit={active} showResult />
              ) : (active.lab_orders || []).length > 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  {rejectedLabs.length > 0
                    ? `The ordered lab tests were not approved${active.lab_approval?.rejection_reason ? ` (${active.lab_approval.rejection_reason})` : ''}. Finalize the visit without test results.`
                    : 'No lab results available.'}
                </div>
              ) : null}
              {completedRads.length > 0 ? (
                <ItemsTable title="Radiology Results (review before prescribing)" items={completedRads} visit={active} showResult />
              ) : (active.rad_orders || []).length > 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  {rejectedRads.length > 0
                    ? `The ordered radiology scans were not approved${active.rad_approval?.rejection_reason ? ` (${active.rad_approval.rejection_reason})` : ''}. Finalize the visit without imaging results.`
                    : 'No radiology results available.'}
                </div>
              ) : null}
              {completedDentals.length > 0 && (
                <ItemsTable title="Dental Results (review before prescribing)" items={completedDentals} visit={active} showResult />
              )}
              {(active.lab_orders || []).length === 0 && (active.rad_orders || []).length === 0 && (active.dental_orders || []).length === 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  No test results available.
                </div>
              )}
              {diagnosis && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm whitespace-pre-line">
                  <span className="font-semibold text-blue-800">Provisional/Differential Diagnosis:</span>{' '}
                  <span className="text-blue-900">{diagnosis}</span>
                </div>
              )}
              {notes && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm whitespace-pre-line" data-testid="review-physical-exam">
                  <span className="font-semibold text-gray-700">Physical Examination (from consultation):</span>{' '}
                  <span className="text-gray-800">{notes}</span>
                </div>
              )}
              <TextArea label="Final Diagnosis (based on the lab results)" required value={finalDiagnosis} onChange={(e) => setFinalDiagnosis(e.target.value)} placeholder="Final diagnosis based on the lab results" testId="review-diagnosis" />
            </>
          ) : (
            <>
              <div className="border border-teal-200 bg-teal-50/40 rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                  <Activity className="w-4 h-4 text-teal-600" /> Vital Signs
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {VITAL_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label className="text-xs text-gray-600">{f.label}</Label>
                      <Input
                        value={vitals[f.key] || ''}
                        onChange={(e) => setVitals((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="h-8 text-sm mt-0.5 bg-white"
                        data-testid={`vitals-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <TextArea label="Patient Complaints" required value={complaints} onChange={(e) => setComplaints(e.target.value)} placeholder="What the patient is complaining of (symptoms, duration…)" testId="consultation-complaints" />
              <TextArea label="Physical Examination" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Physical examination findings" testId="consultation-notes" />
              <TextArea label={(labOrders.length > 0 || radOrders.length > 0 || dentalOrders.length > 0) ? 'Provisional/Differential Diagnosis (final diagnosis comes after test results)' : 'Provisional/Differential Diagnosis'} required value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="Enter the provisional/differential diagnosis" testId="consultation-diagnosis" />
            </>
          )}

          <div>
            <Label className="text-gray-700 font-medium">Order Items (from hospital price list)</Label>
            <Input
              placeholder="Search items by name or ID…"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              className="mt-1"
              data-testid="consultation-item-search"
            />
            {itemSearch && results.length === 0 && <p className="text-xs text-gray-400 mt-1">No matching items</p>}
            {results.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {results.map((item) => (
                  <div key={item.item_id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{item.item_name}</p>
                      <p className="text-xs text-gray-500">{item.item_id}{item.uom ? ` • ${item.uom}` : ''} • {money(active, item.cost)}</p>
                    </div>
                    {/* One single row of buttons on every screen size so Rad and Dental are never stacked */}
                    <div className="grid grid-cols-5 gap-1.5 w-full sm:w-auto sm:flex" data-testid={`item-actions-${item.item_id}`}>
                    {!isReview && (
                      <Button size="sm" variant="outline" className="h-9 sm:h-7 text-xs px-1 sm:px-3 border-purple-300 text-purple-700 hover:bg-purple-50" onClick={() => addLab(item)} data-testid={`add-lab-${item.item_id}`}>
                        <FlaskConical className="w-3 h-3 mr-1 hidden sm:inline" /> Lab
                      </Button>
                    )}
                    {!isReview && (
                      <Button size="sm" variant="outline" className="h-9 sm:h-7 text-xs px-1 sm:px-3 border-sky-300 text-sky-700 hover:bg-sky-50" onClick={() => addRad(item)} data-testid={`add-rad-${item.item_id}`}>
                        <ScanLine className="w-3 h-3 mr-1 hidden sm:inline" /> Rad
                      </Button>
                    )}
                    {!isReview && isSpecialistForm && (
                      <Button size="sm" variant="outline" className="h-9 sm:h-7 text-xs px-1 sm:px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => addPocTest(item)} data-testid={`add-poc-${item.item_id}`}>
                        <Stethoscope className="w-3 h-3 mr-1 hidden sm:inline" /> Test here
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-9 sm:h-7 text-xs px-1 sm:px-3 border-cyan-300 text-cyan-700 hover:bg-cyan-50" onClick={() => addRx(item)} data-testid={`add-rx-${item.item_id}`}>
                      <Pill className="w-3 h-3 mr-1 hidden sm:inline" /> Rx
                    </Button>
                    <Button size="sm" variant="outline" className="h-9 sm:h-7 text-xs px-1 sm:px-3 border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => addItem(setServiceItems)(item)} data-testid={`add-service-${item.item_id}`}>
                      <Briefcase className="w-3 h-3 mr-1 hidden sm:inline" /> Service
                    </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={`grid grid-cols-1 ${isReview ? 'lg:grid-cols-2' : 'lg:grid-cols-2 xl:grid-cols-4'} gap-3`}>
            {!isReview && (
              <BucketList title="Lab Tests" icon={FlaskConical} items={labOrders} setItems={setLabOrders} visit={active} testPrefix="lab-order" />
            )}
            {!isReview && (
              <BucketList title="Radiology / Imaging" icon={ScanLine} items={radOrders} setItems={setRadOrders} visit={active} testPrefix="rad-order" />
            )}
            {!isReview && isSpecialistForm && (
              <div className="bg-white rounded-xl border border-emerald-200 p-3" data-testid="poc-tests-bucket">
                <h4 className="text-sm font-bold text-emerald-800 flex items-center gap-1.5 mb-2"><Stethoscope className="w-4 h-4" /> Tests done here ({pocTests.length})</h4>
                {pocTests.length === 0 ? (
                  <p className="text-xs text-gray-500">Pick a test from the price list and record the result now — no lab visit needed</p>
                ) : pocTests.map((t) => (
                  <div key={t.item_id} className="mb-2 pb-2 border-b border-emerald-100 last:border-0 last:pb-0 last:mb-0" data-testid={`poc-test-${t.item_id}`}>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-gray-800">{t.item_name}</span>
                      <span className="shrink-0 text-xs text-gray-600">{money(active, t.cost)}</span>
                      <button type="button" onClick={() => setPocTests((prev) => prev.filter((x) => x.item_id !== t.item_id))} className="text-red-500 hover:text-red-700" aria-label="Remove" data-testid={`poc-test-remove-${t.item_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <Input value={t.result} onChange={(e) => setPocTests((prev) => prev.map((x) => (x.item_id === t.item_id ? { ...x, result: e.target.value } : x)))} placeholder="Result *" className="mt-1 h-8 text-xs" data-testid={`poc-test-result-${t.item_id}`} />
                  </div>
                ))}
              </div>
            )}
            <BucketList
              title="Prescriptions"
              icon={Pill}
              items={prescriptions}
              setItems={setPrescriptions}
              visit={active}
              withInstructions
              testPrefix="prescription"
              hint={!isReview && (labOrders.length > 0 || radOrders.length > 0 || dentalOrders.length > 0) ? 'Prescribed after test results return' : 'No items added'}
            />
            <BucketList title="Services / Fees" icon={Briefcase} items={serviceItems} setItems={setServiceItems} visit={active} testPrefix="service-item" />
          </div>

          {visitDept(active) === 'general' && !active.external_referral && !(active.departments || []).slice((active.dept_index || 0) + 1).includes('specialist') && (
            referral ? (
              <div className={`p-3 border rounded-lg text-sm flex items-start justify-between gap-2 ${referral.type === 'external' ? 'bg-violet-50 border-violet-200 text-violet-900' : 'bg-indigo-50 border-indigo-200 text-indigo-900'}`} data-testid="referral-banner">
                <div>
                  {referral.type === 'external' ? (
                    <>
                      <strong>External referral:</strong> {referral.specialist} at {referral.facility} <span className="px-1.5 py-0.5 rounded bg-white/70 text-xs font-semibold">{referral.urgency}</span>
                      {referral.doctor_name && <span className="block text-xs mt-0.5">Referring doctor: {referral.doctor_name}</span>}
                      {referral.note && <span className="block text-xs mt-0.5 whitespace-pre-line">{referral.note}</span>}
                      <span className="block text-xs mt-0.5">Needs Acacia office approval — the referral letter becomes printable once approved. The visit continues to pharmacy/billing as usual.</span>
                    </>
                  ) : (
                    <>
                      <strong>Referred to Specialist:</strong> {referral.specialist}
                      {referral.note && <span className="block text-xs text-indigo-700 mt-0.5 whitespace-pre-line">{referral.note}</span>}
                      <span className="block text-xs text-indigo-700 mt-0.5">After this consultation the patient joins the {SPECIALIST_INTERNAL_DEPT[referral.specialist] ? referral.specialist : 'Specialist doctor'}'s queue.</span>
                    </>
                  )}
                </div>
                <button type="button" onClick={clearReferral} className="p-1 rounded hover:bg-black/5" aria-label="Remove referral" data-testid="referral-remove">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => { setReferPick({ type: 'internal', specialist: '', note: '', facility: '', urgency: 'Routine', doctorName: referral?.doctor_name || doctorName.trim() || localStorage.getItem('doctor_full_name') || '' }); setReferDialog(true); }} disabled={submitting} className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="refer-specialist">
                <UserRoundPlus className="w-4 h-4 mr-1" /> Refer Patient to a Specialist (internal or external)
              </Button>
            )
          )}
          <Dialog open={referDialog} onOpenChange={setReferDialog}>
            <DialogContent className="sm:max-w-md" data-testid="referral-dialog">
              <DialogHeader>
                <DialogTitle>Refer {active.patient_name} to a Specialist</DialogTitle>
                <DialogDescription>Internal: the patient moves to the specialist's queue in this hospital when you complete the consultation. External: a referral letter to another facility, printable after Acacia office approval — the patient must report to the Acacia Office first before visiting the referred hospital.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2" data-testid="referral-type">
                  {[['internal', 'Internal (this hospital)'], ['external', 'External (another facility)']].map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setReferPick((p) => ({ ...p, type: k }))} className={`h-10 rounded-md border text-sm font-medium transition-colors ${referPick.type === k ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-testid={`referral-type-${k}`} aria-pressed={referPick.type === k}>{label}</button>
                  ))}
                </div>
                <div>
                  <Label className="text-sm">Specialist <span className="text-red-500">*</span></Label>
                  <select value={referPick.specialist} onChange={(e) => setReferPick((p) => ({ ...p, specialist: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 px-2 text-sm bg-white" data-testid="referral-specialist">
                    <option value="">Choose a specialist…</option>
                    {SPECIALISTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {referPick.type === 'external' && (
                  <>
                    <div>
                      <Label className="text-sm">Referred to hospital / facility <span className="text-red-500">*</span></Label>
                      <Input value={referPick.facility} onChange={(e) => setReferPick((p) => ({ ...p, facility: e.target.value }))} placeholder="e.g. Juba Teaching Hospital" className="mt-1" data-testid="referral-facility" />
                    </div>
                    <div>
                      <Label className="text-sm">Referring doctor's full name (printed on the letter) <span className="text-red-500">*</span></Label>
                      <Input value={referPick.doctorName} onChange={(e) => setReferPick((p) => ({ ...p, doctorName: e.target.value }))} placeholder="e.g. Dr. John Deng Garang" className="mt-1" data-testid="referral-doctor-name" />
                    </div>
                    <div>
                      <Label className="text-sm">Urgency</Label>
                      <select value={referPick.urgency} onChange={(e) => setReferPick((p) => ({ ...p, urgency: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 px-2 text-sm bg-white" data-testid="referral-urgency">
                        {REFERRAL_URGENCIES.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-sm">{referPick.type === 'external' ? 'Reason for referral' : 'Note to the specialist (optional)'}{referPick.type === 'external' && <span className="text-red-500"> *</span>}</Label>
                  <textarea value={referPick.note} onChange={(e) => setReferPick((p) => ({ ...p, note: e.target.value }))} rows={3} className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" placeholder="Reason for referral" data-testid="referral-note" />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setReferDialog(false)}>Cancel</Button>
                <Button onClick={applyReferral} className="bg-indigo-600 hover:bg-indigo-700" data-testid="referral-confirm">Add Referral</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button variant="outline" onClick={() => setAdmitDialog(true)} disabled={submitting} className="w-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="request-admission">
            <BedDouble className="w-4 h-4 mr-1" /> Request Admission (patient too sick to go home)
          </Button>
          <Dialog open={admitDialog} onOpenChange={setAdmitDialog}>
            <DialogContent className="sm:max-w-lg" data-testid="admission-dialog">
              <DialogHeader>
                <DialogTitle>Request Admission — {active.patient_name}</DialogTitle>
                <DialogDescription>The Acacia office must approve the admission. Your notes so far are saved with the request.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Reason for admission <span className="text-red-500">*</span></Label>
                  <textarea value={admit.reason} onChange={(e) => setAdmit((p) => ({ ...p, reason: e.target.value }))} rows={3} className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" data-testid="admission-reason" />
                </div>
                <div>
                  <Label className="text-sm">Ward / bed (optional)</Label>
                  <Input value={admit.ward} onChange={(e) => setAdmit((p) => ({ ...p, ward: e.target.value }))} placeholder="e.g. Female ward, bed 4" className="mt-1" data-testid="admission-ward" />
                </div>
                <div>
                  <Label className="text-sm">Daily bed charge (optional — charged per day until discharge)</Label>
                  <select value={admit.bed_item_id} onChange={(e) => setAdmit((p) => ({ ...p, bed_item_id: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 px-2 text-sm bg-white" data-testid="admission-bed-item">
                    <option value="">No automatic bed charge</option>
                    {bedItems.map((i) => <option key={i.item_id} value={i.item_id}>{i.item_name} — {money(active, i.cost)}/day</option>)}
                  </select>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setAdmitDialog(false)}>Cancel</Button>
                <Button onClick={requestAdmission} disabled={submitting} className="bg-rose-600 hover:bg-rose-700" data-testid="admission-submit">Send for Approval</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            onClick={isReview ? submitReview : submitInitial}
            disabled={submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 h-11"
            data-testid={isReview ? 'review-submit' : 'consultation-submit'}
          >
            {submitting ? 'Saving…' : isReview ? 'Finalize Diagnosis & Prescribe' : (labOrders.length > 0 || radOrders.length > 0 || dentalOrders.length > 0) ? 'Send Patient for Tests' : 'Complete Consultation & Route Patient'}
          </Button>
          {draftSavedAt && (
            <p className="text-xs text-gray-400 text-center -mt-2" data-testid="draft-saved-indicator">
              Draft auto-saved at {draftSavedAt.toLocaleTimeString()} — your work is safe even if this tab closes
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <Stethoscope className="w-5 h-5 text-indigo-600" /> Consultation Queue ({queue.length})
      </h2>
      {queue.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search queue by patient name, serial or visit ID…"
            value={queueSearch}
            onChange={(e) => setQueueSearch(e.target.value)}
            className="pl-10 bg-white"
            data-testid="consultation-queue-search"
          />
        </div>
      )}
      {role === 'Specialist' && (
        localStorage.getItem('specialty') ? (
          <div className="text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2" data-testid="specialty-banner">
            <strong>{localStorage.getItem('specialty')}</strong> queue — you see patients referred to a {localStorage.getItem('specialty')} at your hospital.
          </div>
        ) : (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="specialty-not-set-banner">
            <strong>Specialty not set</strong> — you currently see every specialist referral. Ask the Acacia office to set your specialty under Admin → Users.
          </div>
        )
      )}
      {queue.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500" data-testid="consultation-empty">
          No patients waiting for consultation or results review.
        </div>
      )}
      {queue.length > 0 && filteredQueue.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500" data-testid="consultation-search-empty">
          No patients match "{queueSearch}".
        </div>
      )}
      {admitted.length > 0 && (
        <div className="bg-white rounded-xl border border-rose-200 p-4" data-testid="admitted-patients">
          <h3 className="text-sm font-bold text-rose-800 mb-2 flex items-center gap-1"><BedDouble className="w-4 h-4" /> Admitted Patients ({admitted.length}) — ward rounds, results, discharge</h3>
          <div className="space-y-3">
            {admitted.map((v) => (
              <VisitCard key={v.visit_id} visit={v} showHospital={isSuperAdmin || role === 'ViewOnly'} actionLabel={canAct ? 'Ward Round / Discharge' : null} onAction={canAct ? () => setInpatient(v) : null} actionTestId={`ward-round-${v.visit_id}`} />
            ))}
          </div>
        </div>
      )}
      <TestsInProgress visits={visits.filter((v) => !myDept || visitDept(v) === myDept)} authCfg={authCfg} refresh={refresh} canAct={canAct} />
      {filteredQueue.map((v) => (
        <VisitCard
          key={v.visit_id}
          visit={v}
          showHospital={isSuperAdmin || role === 'ViewOnly'}
          actionLabel={canAct
            ? (v.status === 'WAITING_CONSULTANT' ? 'Start Consultation'
              : v.status === 'CONSULTANT_REVIEW' ? 'Review Results & Prescribe'
                : 'Continue')
            : null}
          onAction={canAct ? () => openVisit(v) : null}
          actionTestId={v.status === 'CONSULTANT_REVIEW' ? `review-results-${v.visit_id}` : `start-consultation-${v.visit_id}`}
        />
      ))}
    </div>
  );
}


function EarlierDoctors({ visit }) {
  const history = visit?.consultation_history || [];
  if (history.length === 0) return null;
  const nextLabel = deptLabel(visitDept(visit), visit);
  return (
    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900 space-y-2" data-testid="earlier-doctors">
      <p className="font-bold">Already seen on this visit by {history.length} doctor{history.length > 1 ? 's' : ''} — you are the {nextLabel}</p>
      {visit.referral && visitDept(visit) === (visit.referral.department || 'specialist') && (
        <p className="whitespace-pre-line" data-testid="referral-info"><span className="font-medium">Referred to {visit.referral.specialist} by {visit.referral.referred_by}</span>{visit.referral.note ? ` — ${visit.referral.note}` : ''}</p>
      )}
      {history.map((c, i) => (
        <div key={i} className="border-t border-emerald-200 pt-2" data-testid={`earlier-doctor-${i}`}>
          <p className="font-semibold">{deptLabel(c.department)} ({c.consultant})</p>
          {c.complaints && <p className="whitespace-pre-line"><span className="font-medium">Complaints:</span> {c.complaints}</p>}
          {c.provisional_diagnosis && <p className="whitespace-pre-line"><span className="font-medium">Provisional Dx:</span> {c.provisional_diagnosis}</p>}
          {c.final_diagnosis && <p className="whitespace-pre-line"><span className="font-medium">Final Dx:</span> {c.final_diagnosis}</p>}
          {c.notes && <p className="whitespace-pre-line"><span className="font-medium">Physical Examination:</span> {c.notes}</p>}
          {formatVitals(c.vitals) && <p><span className="font-medium">Vitals:</span> {formatVitals(c.vitals)}</p>}
        </div>
      ))}
      <p className="text-xs text-emerald-700">Their tests and medicines stay on this visit and will be billed together with yours.</p>
    </div>
  );
}


function TestsInProgress({ visits, authCfg, refresh, canAct }) {
  const inProgress = visits.filter((v) => ['LAB_PENDING', 'RAD_PENDING', 'DENTAL_PENDING', 'DIAGNOSTICS_PENDING'].includes(v.status));
  const [removing, setRemoving] = useState({});
  if (inProgress.length === 0 || !canAct) return null;
  const removeOrder = async (visitId, orderType, order) => {
    if (removing[order.order_id]) return;
    setRemoving((p) => ({ ...p, [order.order_id]: true }));
    try {
      await axios.post(`${API}/visits/${visitId}/orders/remove`, { order_type: orderType, order_id: order.order_id, reason: 'Entered accidentally' }, authCfg);
      toast.success(`Removed "${order.item_name}" from the ${orderType === 'lab' ? 'lab' : orderType === 'rad' ? 'radiology' : 'dental'} queue`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove test');
    } finally {
      setRemoving((p) => ({ ...p, [order.order_id]: false }));
    }
  };
  return (
    <div className="bg-white rounded-xl border border-violet-200 p-4" data-testid="tests-in-progress">
      <h3 className="text-sm font-bold text-violet-800 mb-2">Tests In Progress ({inProgress.length}) — remove a test if it was entered accidentally</h3>
      <div className="space-y-2">
        {inProgress.map((v) => (
          <div key={v.visit_id} className="border border-gray-100 rounded-lg p-2">
            <p className="text-sm font-semibold text-gray-800">{v.patient_name} <span className="text-xs font-normal text-gray-500">({v.visit_id})</span></p>
            {[['lab', v.lab_orders], ['rad', v.rad_orders], ['dental', v.dental_orders]].map(([type, orders]) => (
              (orders || []).filter((o) => o.status === 'pending').map((o) => (
                <div key={o.order_id} className="flex items-center justify-between text-xs text-gray-600 py-1 pl-2">
                  <span>{type === 'lab' ? 'Lab' : type === 'rad' ? 'Radiology' : 'Dental'}: {o.item_name} (x{o.quantity})</span>
                  <button onClick={() => removeOrder(v.visit_id, type, o)} disabled={removing[o.order_id]} className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 font-medium" data-testid={`remove-order-${o.order_id}`}>
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              ))
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
